import React, { useMemo, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  FormControl,
  Autocomplete,
  TextField,
  Paper,
  Button,
  ButtonGroup,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import dayjs from 'dayjs';
import Loading from '../components/loading';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import BusinessHoursLegend from './businessHoursLegend';
import interactionPlugin from '@fullcalendar/interaction';
import { useNavigate } from 'react-router-dom';
import ProfessionalModal from '../register/professional/professionalModal';
import { useProfessionals } from '../hooks/useProfessionals';
import { useProfessionalsList } from '../register/professional/hooks/useProfessionalsList';
import { useCalendarAppointments } from './hooks/useCalendarAppointments';
import { useAgendaBlocks } from './hooks/useAgendaBlocks';
import { usePersonalEvents } from './hooks/usePersonalEvents';
import { useBusinessHours } from './hooks/useBusinessHours';
import { useRoomFilter } from './hooks/useRoomFilter';
import { useHideInactiveAppointments } from './hooks/useHideInactiveAppointments';
import { toBlockEvent } from './agendaBlock/toBlockEvent';
import { toPersonalEvent } from './personalEvent/toPersonalEvent';
import { toAppointmentEvent } from './toAppointmentEvent';
import AppointmentStatusLegend from './appointmentStatusLegend';
import '../css/fullcalendar-mui.css';

const ALL_DENTISTS_OPTION = { id: '', nome: 'Todos os profissionais' };

function AgendaMobile() {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [selectedDentistId, setSelectedDentistId] = useState('');
  const [isDentistModalOpen, setDentistModalOpen] = useState(false);
  const [calendarTitle, setCalendarTitle] = useState('');

  const { professionals: dentists, refetch: refetchDentists } = useProfessionals();
  // /dentists/combobox não traz workingHours; a lista completa alimenta o destaque
  // de expediente do profissional selecionado no calendário.
  const { professionals: dentistsWithWorkingHours, refetch: refetchDentistsWithWorkingHours } =
    useProfessionalsList();
  const {
    appointments: rawAppointments,
    isLoading,
    isStale,
    updatedAt,
  } = useCalendarAppointments(selectedDentistId);
  const { agendaBlocks: rawBlocks } = useAgendaBlocks(selectedDentistId);
  const { personalEvents: rawPersonalEvents } = usePersonalEvents(selectedDentistId);
  const businessHours = useBusinessHours(dentistsWithWorkingHours, selectedDentistId);
  const {
    rooms,
    roomLabel,
    selectedRoomId,
    setSelectedRoomId,
    selectedRoom,
    isRoomFilterVisible,
    clearRoomFilter,
    filterAppointmentsByRoom,
    filterBlocksByRoom,
  } = useRoomFilter();
  const {
    isHidingInactiveAppointments,
    setHidingInactiveAppointments,
    filterInactiveAppointments,
  } = useHideInactiveAppointments();

  const calendarEvents = useMemo(
    () => [
      ...filterInactiveAppointments(filterAppointmentsByRoom(rawAppointments)).map(
        toAppointmentEvent,
      ),
      ...filterBlocksByRoom(rawBlocks).map(toBlockEvent),
      // O recorte por profissional já vem do hook; o filtro de sala não se aplica.
      ...rawPersonalEvents.map(toPersonalEvent),
    ],
    [
      rawAppointments,
      rawBlocks,
      rawPersonalEvents,
      filterAppointmentsByRoom,
      filterBlocksByRoom,
      filterInactiveAppointments,
    ],
  );

  const allRoomsOption = useMemo(() => ({ id: '', name: roomLabel.allOption }), [roomLabel]);

  // Com um único profissional cadastrado o filtro não tem função, mesma regra do
  // filtro de sala. Sem nenhum profissional ele continua visível: o combo carrega
  // o atalho "+ Adicionar profissional".
  const isDentistFilterVisible = dentists.length !== 1;

  // O aviso é sobre o filtro de sala, então olha só o recorte por sala: esconder
  // canceladas e faltas não deve sugerir que a sala está vazia.
  const hasFilteredEverythingOut =
    selectedRoomId !== '' &&
    rawAppointments.length > 0 &&
    filterAppointmentsByRoom(rawAppointments).length === 0;

  function handleNewDentistSaved() {
    refetchDentists();
    refetchDentistsWithWorkingHours();
  }

  const goToEvent = (event) => {
    const { isBlock, isPersonalEvent } = event?.event?.extendedProps ?? {};
    // No mobile bloqueios e compromissos pessoais são apenas exibidos.
    if (isBlock || isPersonalEvent) {
      return;
    }
    const appointmentId = event?.event?.extendedProps?.consultaId;
    if (appointmentId) {
      navigate('/appointment/'.concat(appointmentId));
    } else {
      const hour = String(event.date.getHours()).padStart(2, '0');
      const minute = String(event.date.getMinutes()).padStart(2, '0');
      navigate('/appointment', {
        state: { appointmentData: { hour: `${hour}:${minute}`, date: event.date } },
      });
    }
  };

  const handleDatesSet = (dateInfo) => setCalendarTitle(dateInfo.view.title);
  const handlePrev = () => calendarRef.current?.getApi().prev();
  const handleNext = () => calendarRef.current?.getApi().next();

  // Fallback para dados antigos em cache, que ainda não trazem statusDescription.
  const statusLabel = {
    SCHEDULED: 'Agendada',
    CONFIRMED: 'Confirmada',
    CONFIRMED_BY_PATIENT: 'Confirmada pelo Paciente',
    PERFORMED: 'Realizada',
    NO_SHOW: 'Faltou',
    CANCELED: 'Cancelada',
    REARRANGED: 'Reagendada',
  };

  const renderEventContent = (eventInfo) => {
    const { status, statusDescription } = eventInfo.event.extendedProps;
    const isMonthView = eventInfo.view.type === 'dayGridMonth';
    const eventColor = eventInfo.event.backgroundColor;

    return (
      <Box sx={{ px: 0.5, overflow: 'hidden', lineHeight: 1.3 }}>
        <Box
          sx={{
            fontWeight: 600,
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: isMonthView ? eventColor : 'inherit',
          }}
        >
          {eventInfo.event.title}
        </Box>
        {status && !isMonthView && (
          <Box sx={{ fontSize: '0.7rem', opacity: 0.9, fontStyle: 'italic' }}>
            {statusDescription ?? statusLabel[status] ?? status}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 1 }}>
      <Card sx={{ borderRadius: '16px', boxShadow: 'none' }}>
        <Loading isToHidden={isLoading === false} />

        <CardContent>
          {isStale && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Sem conexão com o servidor. Exibindo os últimos dados salvos
              {updatedAt ? ` (${dayjs(updatedAt).format('DD/MM/YYYY HH:mm')})` : ''}. Pode haver
              agendamentos mais recentes não exibidos.
            </Alert>
          )}

          {isDentistFilterVisible && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  disableClearable
                  size="small"
                  options={[ALL_DENTISTS_OPTION, ...dentists]}
                  getOptionLabel={(option) => option.nome || option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={dentists.find((d) => d.id === selectedDentistId) ?? ALL_DENTISTS_OPTION}
                  onChange={(_, newValue) => setSelectedDentistId(newValue?.id ?? '')}
                  noOptionsText="Nenhum profissional encontrado."
                  PaperComponent={({ children, ...other }) => (
                    <Paper {...other}>
                      {children}
                      <Box
                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <Button fullWidth size="small" onClick={() => setDentistModalOpen(true)}>
                          + Adicionar profissional
                        </Button>
                      </Box>
                    </Paper>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="dentist-select"
                      label="Selecione o Profissional"
                      inputProps={{ ...params.inputProps, 'data-cy': 'dentist-filter' }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          )}

          {isRoomFilterVisible && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  disableClearable
                  size="small"
                  options={[allRoomsOption, ...rooms]}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedRoom ?? allRoomsOption}
                  onChange={(_, newValue) => setSelectedRoomId(newValue?.id ?? '')}
                  noOptionsText={roomLabel.emptyMessage}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="room-select"
                      label={`Selecione o ${roomLabel.singular}`}
                      inputProps={{ ...params.inputProps, 'data-cy': 'room-filter' }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          )}

          {hasFilteredEverythingOut && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={clearRoomFilter}>
                  {roomLabel.allOption}
                </Button>
              }
            >
              Nenhuma consulta neste período para {selectedRoom?.name}.
            </Alert>
          )}

          {/* Toolbar compacta para mobile */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={handlePrev} sx={{ px: 0.5, minWidth: 36 }}>
                <ChevronLeftIcon fontSize="small" />
              </Button>
              <Button onClick={handleNext} sx={{ px: 0.5, minWidth: 36 }}>
                <ChevronRightIcon fontSize="small" />
              </Button>
            </ButtonGroup>

            <Typography variant="body2" fontWeight={600}>
              {calendarTitle}
            </Typography>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Checkbox
                size="small"
                data-cy="hide-inactive-appointments"
                checked={isHidingInactiveAppointments}
                onChange={(event) => setHidingInactiveAppointments(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Esconder canceladas e faltas
              </Typography>
            }
          />

          <AppointmentStatusLegend />

          {businessHours && <BusinessHoursLegend />}

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridDay"
            locale="pt-br"
            contentHeight="auto"
            headerToolbar={false}
            businessHours={businessHours}
            slotMinTime="06:00:00"
            slotMaxTime="21:00:00"
            allDayText="Dia Inteiro"
            events={calendarEvents}
            datesSet={handleDatesSet}
            eventContent={renderEventContent}
            dateClick={(event) => goToEvent(event)}
            eventClick={(event) => goToEvent(event)}
          />
        </CardContent>
      </Card>
      <ProfessionalModal
        open={isDentistModalOpen}
        onClose={() => setDentistModalOpen(false)}
        onSave={handleNewDentistSaved}
      />
    </Box>
  );
}

export default AgendaMobile;
